/*
 * Copyright 2026 Oddpods
 *
 * This Source Code Form is subject to the terms of the Mozilla Public
 * License, v. 2.0. If a copy of the MPL was not distributed with this
 * file, You can obtain one at https://mozilla.org/MPL/2.0/.
 *
 * See https://github.com/oddpods/cauce/blob/main/LICENSE
 */
package studio.oddpods.cauce.feature.onboarding.painid

import androidx.lifecycle.viewModelScope
import kotlinx.coroutines.launch
import studio.oddpods.cauce.core.base.analytics.AnalyticsHelper
import studio.oddpods.cauce.core.base.analytics.OnboardingEvents
import studio.oddpods.cauce.core.base.ui.BaseViewModel
import studio.oddpods.cauce.core.data.repository.cauce.ProfileRepository
import studio.oddpods.cauce.core.model.profile.OnboardingStep

enum class PainOption(val serialKey: String) {
    COBROS("cobros"),
    AFIP("afip"),
    DEUDA_TOTAL("deuda_total"),
    RECORDATORIOS("recordatorios"),
    REGISTRO("registro"),
}

data class PainIdState(
    val selected: Set<PainOption> = emptySet(),
    val enteredAtMs: Long = 0L,
)

sealed class PainIdAction {
    data class Toggle(val option: PainOption) : PainIdAction()
    data object ContinueClick : PainIdAction()
}

sealed class PainIdEvent {
    data object NavigateNext : PainIdEvent()
}

class PainIdViewModel(
    private val profileRepository: ProfileRepository,
    private val analyticsHelper: AnalyticsHelper,
    private val currentTimeMs: () -> Long = { kotlin.time.Clock.System.now().toEpochMilliseconds() },
) : BaseViewModel<PainIdState, PainIdEvent, PainIdAction>(PainIdState(enteredAtMs = currentTimeMs())) {

    override fun handleAction(action: PainIdAction) {
        when (action) {
            is PainIdAction.Toggle -> {
                val next = state.selected.toMutableSet().also {
                    if (!it.add(action.option)) it.remove(action.option)
                }
                mutableStateFlow.value = state.copy(selected = next)
            }
            PainIdAction.ContinueClick -> {
                val screenTimeSeconds =
                    ((currentTimeMs() - state.enteredAtMs) / 1000L).toInt().coerceAtLeast(0)
                val keys = PainOption.entries.filter { it in state.selected }.map { it.serialKey }
                analyticsHelper.logEvent(OnboardingEvents.painSelected(keys, screenTimeSeconds))
                viewModelScope.launch {
                    profileRepository.setOnboardingStep(OnboardingStep.PROFILE)
                    sendEvent(PainIdEvent.NavigateNext)
                }
            }
        }
    }
}
